import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { AUTH } from '@/constants/testIds';
import ThemeToggle from '@/components/ThemeToggle';
import { Eye, EyeOff, ShieldCheck, ArrowLeft, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { apiErrorMessage } from '@/lib/api';

export default function Login() {
    const { login, completeMfaLogin, loading } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPw, setShowPw] = useState(false);
    const [mfaChallenge, setMfaChallenge] = useState(null);
    const [mfaCode, setMfaCode] = useState('');
    const [useRecovery, setUseRecovery] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        try {
            const result = await login(email, password);
            if (result?.mfaRequired) {
                setMfaChallenge(result.mfaChallenge);
                return;
            }
            toast.success(`Bienvenido, ${result.name}`);
            navigate(`/${result.role}`);
        } catch (err) {
            toast.error(apiErrorMessage(err, 'Error al iniciar sesión'));
        }
    };

    const submitMfa = async (e) => {
        e.preventDefault();
        try {
            const user = await completeMfaLogin(mfaChallenge, mfaCode);
            toast.success(`Bienvenido, ${user.name}`);
            navigate(`/${user.role}`);
        } catch (err) {
            toast.error(apiErrorMessage(err, 'Código de verificación inválido'));
        }
    };

    return (
        <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-white">
            <div className="hidden lg:flex flex-col justify-between p-12 bg-black text-white">
                <Link to="/" className="flex items-center">
                    <img src="https://customer-assets.emergentagent.com/job_audit-ia-1/artifacts/6tkmr7v8_3.png" alt="AIGIS CERTIFICATE · Safety Solutions" className="h-20 w-auto object-contain" />
                </Link>
                <div>
                    <div className="label-xs mb-3 text-[#E08B3C]">Acceso unificado</div>
                    <h2 className="font-display text-4xl font-black leading-tight mb-6">
                        Cliente, Auditor o Administrador. Un solo punto de entrada.
                    </h2>
                    <p className="text-white/70 leading-relaxed max-w-md">
                        El sistema detectará tu rol y te llevará al panel correspondiente,
                        con su propia paleta de control y permisos.
                    </p>
                </div>
                <div className="text-xs text-white/50">© 2026 AIGIS CERTIFICATE</div>
            </div>

            <div className="flex items-center justify-center p-6 lg:p-12 relative">
                <div className="absolute top-4 right-4 z-10">
                    <ThemeToggle />
                </div>

                {mfaChallenge ? (
                    <form onSubmit={submitMfa}
                        className="w-full max-w-md border border-gray-200 p-8 bg-white"
                        data-testid="login-mfa-form">
                        <button type="button" onClick={() => { setMfaChallenge(null); setMfaCode(''); setUseRecovery(false); }}
                            data-testid="login-mfa-back-button"
                            className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-black mb-4">
                            <ArrowLeft className="w-3.5 h-3.5" /> Volver
                        </button>
                        <div className="label-xs mb-2 flex items-center gap-2">
                            <ShieldCheck className="w-4 h-4" /> Verificación en dos pasos
                        </div>
                        <h1 className="font-display text-3xl font-bold tracking-tight mb-2">Confirma tu identidad</h1>
                        <p className="text-sm text-gray-500 mb-6">
                            {useRecovery
                                ? 'Ingresa uno de tus códigos de respaldo de un solo uso.'
                                : 'Ingresa el código de 6 dígitos de tu app de autenticación.'}
                        </p>

                        <label className="text-sm font-medium block mb-1" htmlFor="mfa-code">
                            {useRecovery ? 'Código de respaldo' : 'Código de 6 dígitos'}
                        </label>
                        <input
                            id="mfa-code"
                            autoFocus
                            required
                            value={mfaCode}
                            onChange={(e) => setMfaCode(useRecovery ? e.target.value.toUpperCase() : e.target.value.replace(/\D/g, '').slice(0, 6))}
                            placeholder={useRecovery ? 'AB12-CD34' : '123456'}
                            inputMode={useRecovery ? 'text' : 'numeric'}
                            autoComplete="one-time-code"
                            data-testid="login-mfa-code-input"
                            className="w-full border border-gray-300 px-4 py-3 mb-4 tracking-widest text-center font-mono text-lg focus:border-black focus:ring-1 focus:ring-black outline-none"
                        />

                        <button
                            type="submit"
                            disabled={loading || !mfaCode}
                            data-testid="login-mfa-submit-button"
                            className="w-full bg-[#111111] hover:bg-black text-white py-3 font-medium transition-colors disabled:opacity-50"
                        >
                            {loading ? 'Verificando...' : 'Verificar y entrar'}
                        </button>

                        <button type="button"
                            onClick={() => { setUseRecovery((v) => !v); setMfaCode(''); }}
                            data-testid="login-mfa-toggle-recovery"
                            className="mt-4 text-sm text-[#1F8FB8] hover:underline w-full text-center">
                            {useRecovery ? 'Usar código de mi app de autenticación' : '¿Perdiste tu celular? Usa un código de respaldo'}
                        </button>
                    </form>
                ) : (
                <form onSubmit={submit}
                    className="w-full max-w-md border border-gray-200 p-8 bg-white"
                    data-testid="login-form"
                    autoComplete="off">
                    <div className="label-xs mb-2">Iniciar sesión — Acceso unificado</div>
                    <h1 className="font-display text-3xl font-bold tracking-tight mb-6">Bienvenido de nuevo</h1>

                    <label className="text-sm font-medium block mb-1" htmlFor="login-email">Correo electrónico</label>
                    <input
                        id="login-email"
                        name="login-email"
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        autoComplete="off"
                        data-testid={AUTH.emailInput}
                        placeholder="tu@empresa.com"
                        className="w-full border border-gray-300 px-4 py-3 mb-4 focus:border-black focus:ring-1 focus:ring-black outline-none"
                    />

                    <label className="text-sm font-medium block mb-1" htmlFor="login-password">Contraseña</label>
                    <div className="relative mb-6">
                        <input
                            id="login-password"
                            name="login-password"
                            type={showPw ? 'text' : 'password'}
                            required
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            autoComplete="new-password"
                            data-testid={AUTH.passwordInput}
                            placeholder="••••••••"
                            className="w-full border border-gray-300 px-4 py-3 pr-12 focus:border-black focus:ring-1 focus:ring-black outline-none"
                        />
                        <button
                            type="button"
                            onClick={() => setShowPw((v) => !v)}
                            aria-label={showPw ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                            title={showPw ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                            data-testid="login-toggle-password"
                            className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-700 focus:outline-none"
                        >
                            {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                    </div>

                    <button
                        type="submit"
                        disabled={loading}
                        data-testid={AUTH.submitButton}
                        className="w-full bg-[#111111] hover:bg-black text-white py-3 font-medium transition-colors disabled:opacity-50"
                    >
                        {loading ? 'Ingresando...' : 'Ingresar'}
                    </button>

                    <div className="mt-6 text-sm text-gray-600 text-center">
                        ¿No tienes cuenta?{' '}
                        <a href="/#contacto" data-testid={AUTH.toggleRegister} className="text-[#1F8FB8] hover:underline font-medium">
                            El alta la realiza el administrador — contáctanos
                        </a>
                    </div>
                </form>
                )}
            </div>
        </div>
    );
}
